import HubLayout from '../components/hubs/HubLayout'
import HubEventTile from '../components/hubs/HubEventTile'
import ProducerAvatar from '../components/producers/ProducerAvatar'
import { NextSeo } from 'next-seo'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import Image from 'next/image'
import { Hub, HubColors } from '../interfaces/index'
import { fetchHubAndProducer } from '../lib/hubs'
import { cleanObjectEntries } from '../utils/clean_object_entries'

export const getServerSideProps: GetServerSideProps<{ hub?: Hub }> = async (context) => {
  const { hubCode } = context.query
  const hub = await fetchHubAndProducer(hubCode as string)

  return {
    props: hub ? { hub: cleanObjectEntries(hub) } : {},
  }
}

const HubPage = ({ hub }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  if (!hub) return (<HubLayout>Hub not found</HubLayout>)

  const coverImage = hub.cover_image_url ? (
    <>
      <div className="flex flex-col items-center mt-0 sm:mt-8">
        <Image className="max-w-2xl w-full" src={hub.cover_image_url} alt={`${hub.name} cover image`} width={640} height={360} objectFit="contain" />
      </div>
    </>
  ) : null

  const paragraphs = (
    <div className="text-center px-12">
      {hub.description.split("\n").map((paragraph, index) => (
        <p key={`${hub.id}-description-${index}`} className="my-8" style={{ color: hub.text_color_hex }}>
          {paragraph}
        </p>
      ))}
    </div>
  )

  const hubColors: HubColors = {
    primary: hub.primary_color_hex,
    background: hub.background_color_hex,
    foreground: hub.text_color_hex,
  }

  const hubEventTiles = hub.events.map((event) => (
    <HubEventTile
      key={event.id}
      hub={hub}
      event={event}
      hubColors={hubColors}
    />
  ));

  const hubEvents = hub.events.length == 1 ? (
    <div className="grid">
      {hubEventTiles}
    </div>
  ) : hub.events.length == 2 ? (
    <div className="grid md:grid-cols-2">
      {hubEventTiles}
    </div>
  ) : (
    <div className="grid md:grid-cols-2 xl:grid-cols-3">
      {hubEventTiles}
    </div>
  );

  return (
    <>
      <HubLayout title={hub.name} hubColors={hubColors}>
        {coverImage}
        {paragraphs}

        <div className="container mx-auto p-4">
          {hubEvents}
        </div>

        <div className="container mx-auto mt-4">
          <ProducerAvatar producer={hub.producer} hubColors={hubColors} />
        </div>
      </HubLayout>
      <NextSeo
        title={hub.name}
        description={hub.description}
        canonical="https://www.canonical.ie/"
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_HUBS_BASE_URI}/${hub.code}`,
          title: hub.name,
          description: hub.description,
          images: hub.cover_image_url ? [{
            url: hub.cover_image_url,
            width: 800,
            height: 450,
            alt: `${hub.name} event image`,
          }] : [],
          site_name: 'KiwiTicket',
        }}
        twitter={{
          handle: '@kiwiticketnz',
          site: '@kiwiticketnz',
          cardType: 'summary_large_image',
        }}
      />
    </>
  )
}

export default HubPage
